/**
 * AudioProvider - Always-mounted audio state for live feed playback
 *
 * Subscribes to LiveFeedProvider for messages. Two modes:
 * - Drip mode (Live page mounted): drip function calls enqueueAudio() for sync
 * - Direct mode (off Live page): auto-enqueues from LiveFeedProvider subscriber
 *
 * Controls LiveFeedProvider's WS lifecycle via setChannelIds/setWsEnabled.
 *
 * Provider stack requirement:
 *   OrgProvider > LiveFeedProvider > AudioProvider
 */

import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { useOrg } from './OrgProvider';
import { useLiveFeed } from './LiveFeedProvider';
import { getMessageAudio } from '../../api/server/messages';
import { logger } from '../../utils/logger';

// ---------------------------------------------------------------------------
// Context interface
// ---------------------------------------------------------------------------

export interface AudioContextValue {
  isLiveActive: boolean;
  hasBeenLive: boolean;
  volume: number;
  listeningChannelIds: string[];
  currentlyPlayingTalkId: string | null;
  pausedAt: string | null;

  play: () => void;
  pause: (at?: string) => void;
  toggle: () => void;
  setVolume: (v: number) => void;
  setListeningChannelIds: (ids: string[]) => void;
  enqueueAudio: (talkId: string, duration: number, created: number) => void;
  /** Live page sets true on mount, false on unmount — suppresses auto-enqueue */
  setDripActive: (active: boolean) => void;
}

const AudioContext = createContext<AudioContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface AudioProviderProps {
  children: React.ReactNode;
}

export function AudioProvider({ children }: AudioProviderProps) {
  const { currentOrg } = useOrg();
  const liveFeed = useLiveFeed();

  const orgId = currentOrg?.org_id;

  const [isLiveActive, setIsLiveActive] = useState(false);
  const [hasBeenLive, setHasBeenLive] = useState(false);
  const [volume, setVolumeState] = useState(1);
  const [pausedAt, setPausedAt] = useState<string | null>(null);

  // Reset live state on org switch so new orgs get the muted-on-first-visit default.
  const prevOrgIdRef = useRef(orgId);
  useEffect(() => {
    if (orgId && orgId !== prevOrgIdRef.current) {
      prevOrgIdRef.current = orgId;
      setHasBeenLive(false);
      setIsLiveActive(false);
      setVolumeState(1);
      setPausedAt(null);
    }
  }, [orgId]);
  const [listeningChannelIds, setListeningChannelIdsState] = useState<string[]>([]);
  const [currentlyPlayingTalkId, setCurrentlyPlayingTalkId] = useState<string | null>(null);

  const audioUrlRef = useRef<string>('');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPlayingRef = useRef(false);
  const dripActiveRef = useRef(false);

  const queueRef = useRef<{ talkId: string; retries: number; delay: number; created: number }[]>(
    []
  );
  const enqueuedIdsRef = useRef(new Set<string>());

  // ---------------------------------------------------------------------------
  // Sync WS lifecycle with AudioProvider state
  // ---------------------------------------------------------------------------

  const setListeningChannelIds = useCallback(
    (ids: string[]) => {
      setListeningChannelIdsState(ids);
      liveFeed.setChannelIds(ids);
    },
    [liveFeed.setChannelIds]
  );

  const [dripActive, setDripActiveState] = useState(false);

  useEffect(() => {
    liveFeed.setWsEnabled(isLiveActive || dripActive);
  }, [isLiveActive, dripActive, liveFeed.setWsEnabled]);

  // ---------------------------------------------------------------------------
  // Sequential audio queue
  // ---------------------------------------------------------------------------

  const orgIdRef = useRef(orgId);
  orgIdRef.current = orgId;

  const playNextRef = useRef<() => void>(() => {});

  const loadAndPlay = useCallback(async (talkId: string, retries: number) => {
    if (!orgIdRef.current) {
      playNextRef.current();
      return;
    }
    isPlayingRef.current = true;
    try {
      const res = await getMessageAudio(talkId, orgIdRef.current);
      if (res && res instanceof Blob && res.size > 0) {
        if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
        const url = URL.createObjectURL(res);
        audioUrlRef.current = url;
        setCurrentlyPlayingTalkId(talkId);
        retryCountRef.current = 0;
        if (audioRef.current) {
          audioRef.current.src = url;
          audioRef.current.play().catch((err) => {
            logger.error('[AudioProvider] Failed to play audio:', err);
            playNextRef.current();
          });
        }
      } else if (retryCountRef.current < retries) {
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
          retryCountRef.current += 1;
          loadAndPlay(talkId, retries);
        }, 750);
      } else {
        playNextRef.current();
      }
    } catch (err) {
      logger.error('[AudioProvider] Failed to load audio:', err);
      if (retryCountRef.current < retries) {
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
          retryCountRef.current += 1;
          loadAndPlay(talkId, retries);
        }, 750);
      } else {
        playNextRef.current();
      }
    }
  }, []);

  const playNext = useCallback(() => {
    if (queueRef.current.length === 0) {
      isPlayingRef.current = false;
      setCurrentlyPlayingTalkId(null);
      return;
    }
    const next = queueRef.current.shift()!;
    if (next.delay > 0) {
      retryTimerRef.current = setTimeout(() => {
        retryCountRef.current = 0;
        loadAndPlay(next.talkId, next.retries);
      }, next.delay);
    } else {
      retryCountRef.current = 0;
      loadAndPlay(next.talkId, next.retries);
    }
  }, [loadAndPlay]);

  playNextRef.current = playNext;

  const enqueueAudio = useCallback(
    (talkId: string, duration: number, created: number) => {
      if (enqueuedIdsRef.current.has(talkId)) return;
      enqueuedIdsRef.current.add(talkId);

      const retries = duration > 0 ? 10 : 5;
      const delay = duration > 0 ? 0 : 1000;
      const entry = { talkId, retries, delay, created };
      const q = queueRef.current;

      let i = q.length;
      while (i > 0 && q[i - 1].created > created) i--;
      q.splice(i, 0, entry);

      if (!isPlayingRef.current) {
        isPlayingRef.current = true;
        const next = q.shift()!;
        retryCountRef.current = 0;
        if (next.delay > 0) {
          retryTimerRef.current = setTimeout(() => {
            loadAndPlay(next.talkId, next.retries);
          }, next.delay);
        } else {
          loadAndPlay(next.talkId, next.retries);
        }
      }
    },
    [loadAndPlay]
  );

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const handleEnded = () => playNext();
    el.addEventListener('ended', handleEnded);
    return () => el.removeEventListener('ended', handleEnded);
  }, [playNext]);

  // ---------------------------------------------------------------------------
  // Subscribe to LiveFeedProvider — auto-enqueue when drip is not active
  // ---------------------------------------------------------------------------

  const enqueueAudioRef = useRef(enqueueAudio);
  enqueueAudioRef.current = enqueueAudio;

  const isLiveActiveRef = useRef(isLiveActive);
  isLiveActiveRef.current = isLiveActive;

  useEffect(() => {
    const unsub = liveFeed.subscribe((msg) => {
      if (!isLiveActiveRef.current) return;
      if (dripActiveRef.current) return;
      if (!msg.talk_id) return;
      const created = new Date(msg.created).getTime();
      enqueueAudioRef.current(msg.talk_id, msg.duration ?? 0, created);
    });
    return unsub;
  }, [liveFeed.subscribe]);

  // ---------------------------------------------------------------------------
  // Cleanup on unmount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  const clearAudio = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    retryCountRef.current = 0;
    queueRef.current = [];
    enqueuedIdsRef.current.clear();
    isPlayingRef.current = false;
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = '';
    }
    setCurrentlyPlayingTalkId(null);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
  }, []);

  const play = useCallback(() => {
    setIsLiveActive(true);
    setHasBeenLive(true);
    setPausedAt(null);
  }, []);

  const pause = useCallback(
    (at?: string) => {
      setIsLiveActive(false);
      setPausedAt(at ?? new Date().toISOString());
      clearAudio();
    },
    [clearAudio]
  );

  const toggle = useCallback(() => {
    if (isLiveActiveRef.current) {
      clearAudio();
      setIsLiveActive(false);
      setPausedAt(new Date().toISOString());
    } else {
      setIsLiveActive(true);
      setHasBeenLive(true);
      setPausedAt(null);
    }
  }, [clearAudio]);

  const setVolume = useCallback((newVolume: number) => {
    setVolumeState(newVolume);
    if (audioRef.current) audioRef.current.volume = newVolume;
  }, []);

  const setDripActive = useCallback((active: boolean) => {
    dripActiveRef.current = active;
    setDripActiveState(active);
  }, []);

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------

  const value = useMemo<AudioContextValue>(
    () => ({
      isLiveActive,
      hasBeenLive,
      volume,
      listeningChannelIds,
      currentlyPlayingTalkId,
      pausedAt,
      play,
      pause,
      toggle,
      setVolume,
      setListeningChannelIds,
      enqueueAudio,
      setDripActive
    }),
    [
      isLiveActive,
      hasBeenLive,
      volume,
      listeningChannelIds,
      currentlyPlayingTalkId,
      pausedAt,
      play,
      pause,
      toggle,
      setVolume,
      setListeningChannelIds,
      enqueueAudio,
      setDripActive
    ]
  );

  return (
    <AudioContext.Provider value={value}>
      <audio ref={audioRef} className="hidden" />
      {children}
    </AudioContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAudio(): AudioContextValue {
  const context = useContext(AudioContext);
  if (context === undefined) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
}

export default AudioProvider;
